import {
  getAccessToken,
  getRefreshToken,
  setAppTokens,
} from "@/lib/api/auth-token";
import { exchangeRefreshToken } from "@/lib/api/token-refresh-api";
import { getJwtExpiresInSeconds, isAccessTokenExpired } from "@/lib/auth/jwt-utils";
import type { AppTokenPair } from "@/types/auth-api-types";

let refreshInFlight: Promise<AppTokenPair | null> | null = null;

/** Persist tokens from SAML callback query params (application JWT pair). */
export function storeTokensFromHandoff(
  accessToken: string,
  refreshToken?: string | null,
): void {
  setAppTokens({
    accessToken: accessToken.trim(),
    refreshToken: refreshToken?.trim() || undefined,
    expiresIn: getJwtExpiresInSeconds(accessToken.trim()) ?? undefined,
  });
}

/** Exchange the stored refresh token for a new JWT pair (deduped). */
export async function refreshAppTokens(): Promise<AppTokenPair | null> {
  const refreshToken = getRefreshToken()?.trim();
  if (!refreshToken) return null;

  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    try {
      const tokens = await exchangeRefreshToken(refreshToken);
      setAppTokens(tokens);
      return tokens;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Returns a usable access JWT, refreshing when expired or near expiry.
 * Returns null when no session can be established.
 */
export async function getValidAccessToken(): Promise<string | null> {
  const accessToken = getAccessToken()?.trim();
  if (accessToken && !isAccessTokenExpired(accessToken)) {
    return accessToken;
  }

  const refreshed = await refreshAppTokens();
  return refreshed?.accessToken?.trim() ?? null;
}

/** On app load: refresh an expired access token when a refresh token exists. */
export async function ensureValidAccessToken(): Promise<boolean> {
  const accessToken = getAccessToken()?.trim();
  if (accessToken && !isAccessTokenExpired(accessToken)) {
    return true;
  }

  const refreshed = await refreshAppTokens();
  return Boolean(refreshed?.accessToken?.trim());
}
